import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Theme,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridSortModel } from '@mui/x-data-grid';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useRecoilState, useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';

import { contactSearch } from '../../recoil/atoms/contactSearch';
import { contactsPage } from '../../recoil/atoms/contactsPage';
import { IBusinessModel, IUserAtom, user } from '../../recoil/atoms/user';
import { getContacts } from '../../recoil/selectors/getContacts';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { ModalWithStyles } from '../components/modals/ModalWithStyles';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { CRMDisplayNameMap, CRMNameMap } from '../constants/CRMNameMap';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const VITE_APP_CONTACT_SYNC = process.env.VITE_APP_CONTACT_SYNC;

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  alignRight: {
    justifyContent: 'flex-end',
  },
  callMadeIcon: {
    color: '#26c281',
  },
  playIcon: {
    color: '#e87e04',
    marginRight: theme.spacing(1),
  },
  callMissedIcon: {
    color: '#d91e18',
  },
  messageIcon: {
    color: '#0098C7',
    marginRight: theme.spacing(1),
  },
  linkIcon: {
    color: '#337ab7',
    marginRight: theme.spacing(1),
  },
  phoneNumber: {
    '& .historyc2c.link.pointer': {
      display: 'block',
    },
  },
  select: {
    '& legend': {
      visibility: 'visible',
    },
    '& fieldset': {
      borderColor: 'black',
    },
  },
  audio: {
    transition: 'all 0.5s linear',
    boxShadow: '0px 0px 0px 0px',
    backgroundColor: '#f0f3f4',
    borderRadius: '7px 7px 7px 7px',
    height: 32,
    maxWidth: '-webkit-fill-available',
    margin: '0px auto',
    '&:focus': {
      outline: 0,
    },
  },
  audioContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  audioDetails: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingLeft: '35px',
  },
  audioBullet: {
    width: '50%',
  },
  button: {
    height: '35px',
    marginLeft: '4px',
    marginRight: '4px',
    marginTop: '5px', // zero out material-ui dense
    marginBottom: 0, // zero out material-ui dense
  },
  buttonDisabled: {
    backgroundColor: 'rgb(215,215,215) !important',
    color: 'black !important',
    height: '35px',
    marginLeft: '4px',
    marginRight: '4px',
    marginTop: '5px', // zero out material-ui dense
    marginBottom: 0, // zero out material-ui dense
  },
}));

export const ManageContacts = ({ agentId }: { agentId: any }) => {
  const classes = useStyles();
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const {
    role,
    callRecord,
    canInitiateContactSync,
  } = permissionsLoadable.contents as IPermissions;
  const [page, setPage] = useRecoilState(contactsPage);
  const [search, setSearch] = useRecoilState(contactSearch);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedCRM, setSelectedCRM] = useState<string>('None');
  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ]);
  /**
   * Reset page start on search
   *c
   * @param value Search string
   */
  const handleSearchChange = useCallback(
    (value: string) => {
      // reset page back to 1
      setPage(1);
      setSearch(value);
    },
    [setSearch]
  );

  const [_, businessDataLoadable] = makeApiRequestCached<
    IBusinessModel | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    })
  );

  const activeBusinessCRMsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: 'legacy/getActiveBusinessCRMsByAccountId',
      payloadJsonString: JSON.stringify({
        call: 'getActiveBusinessCRMsByAccountId',
      }),
    })
  );

  const activeCRMs = useMemo(() => {
    const crmIds =
      activeBusinessCRMsLoadable?.contents?.response?.data?.result ?? [];

    let activeCRMsArray = crmIds.map(
      (elem: { crmID: number; orgid: string | null }) => {
        return {
          accountid: elem.orgid,
          id: elem.crmID,
          displayName: `${CRMDisplayNameMap[CRMNameMap[elem.crmID]]} ${
            elem.orgid ? '(' + elem.orgid + ')' : ''
          }`,
        };
      }
    );

    activeCRMsArray.push({
      accountid: '',
      id: 0,
      displayName: 'None',
    });

    activeCRMsArray = activeCRMsArray.filter((elem) => {
      return !!elem.displayName;
    });

    return activeCRMsArray;
  }, [activeBusinessCRMsLoadable?.contents?.response?.data?.result]);

  const contacts = useRecoilValueLoadable<any>(
    getContacts({
      page,
      pageSize,
      sortModel: {
        ...sortModel?.[0],
      },
      search,
      //   agentId,
    })
  );
  const total = contacts.contents?.data?.total ?? 0;

  const handlePageChange = (page: number, details?: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number, details?: any) => {
    setPageSize(pageSize);
  };

  const handleGridSortModelChange = (model: GridSortModel, details?: any) => {
    setGridSortModel(sortModel);
  };

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtom.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));

  const handleSync = debounce(async (shouldDelete?: boolean) => {
    const syncCRM = activeCRMs.find((elem) => elem.displayName === selectedCRM);
    try {
      setConfirmDelete(false);
      setSyncLoading(true);
      const response = await mutation.mutateAsync({
        domain: process.env.VITE_APP_CDK_DOMAIN,
        endpoint: endpointEnum.SYNC_CONTACTS,
        method: 'post',
        payload: {
          delete: shouldDelete,
          businessId: userAtomContents.businessid,
          userId: userAtomContents.userid,
          crmId: process.env.VITE_APP_CONTACT_SYNC === '1' ? syncCRM.id : 16,
          accountid: syncCRM.accountid ?? '',
          hubspotOnly: process.env.VITE_APP_CONTACT_SYNC !== '1',
        },
      });
      setSyncLoading(false);
      if (response?.status === 200) {
        enqueueSnackbarWithStyles({
          message: 'Successfully synced contacts',
          alertProps: { severity: 'success' },
        });
      } else if (response.status === 418) {
        enqueueSnackbarWithStyles({
          message: 'This functionality is not supported for this CRM',
          alertProps: { severity: 'warning' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message:
            'Something went wrong sending the sync request to the server',
          alertProps: { severity: 'error' },
        });
      }
    } catch (error: any) {
      setSyncLoading(false);
      if (
        error?.response?.data?.errorMessage &&
        error?.response?.data?.errorMessage !== ''
      ) {
        enqueueSnackbarWithStyles({
          message: `${error?.response?.data?.errorMessage}`,
          alertProps: { severity: 'error' },
        });
      } else {
        const errorMessage = error.message as string;
        if (errorMessage.includes('418')) {
          enqueueSnackbarWithStyles({
            message: 'This functionality is not supported for this CRM',
            alertProps: { severity: 'warning' },
          });
        } else if (errorMessage.includes('400')) {
          enqueueSnackbarWithStyles({
            message:
              'There is a sync in progress for the current business. Please try again later.',
            alertProps: { severity: 'error' },
          });
        } else {
          enqueueSnackbarWithStyles({
            message:
              'Something went wrong sending the sync request to the server',
            alertProps: { severity: 'error' },
          });
        }
      }
    }
  }, 500);

  const rowsCached = useRef([]);
  /**
   * Transform by adding id
   * NOTE: must use useMemo so rows do not get updated on re-render
   */
  rowsCached.current = useMemo(
    () =>
      contacts.state !== 'loading'
        ? contacts?.contents?.data?.contacts?.map((row: any) => ({
            ...row,
            id: row.externalid,
          }))
        : rowsCached.current,
    [contacts]
  );

  const disableButtons =
    process.env.VITE_APP_CONTACT_SYNC === '1'
      ? selectedCRM === 'None' || syncLoading
      : false;
  /**
   * Create cutom toolbar component for table
   * Use useCallback to stop rerendering
   */
  const ToolbarComponent = (props: IToolbarComponent) => (
    <EnhancedTableToolbar
      {...props}
      search={search}
      setSearch={handleSearchChange}
      debounceTimeout={500} // add 1/2 second debounce vs 100ms default value
      isLoading={contacts.state === 'loading'} // do not block on search
      dataType="Calls"
      rightComponent={
        <>
          {!!canInitiateContactSync && (
            <div
              className="superStyle"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {process.env.VITE_APP_CONTACT_SYNC === '1' &&
              activeCRMs?.length ? (
                <FormControl
                  style={{
                    width: '200px',
                    marginLeft: '4px',
                    marginRight: '4px',
                  }}
                  size="small"
                  variant="outlined"
                >
                  {/* <InputLabel>Select CRM</InputLabel> */}
                  <Select
                    className={classes.select}
                    style={{ color: 'black', width: '100%', minWidth: '80px' }}
                    label="Select CRM"
                    size="small"
                    variant="outlined"
                    value={selectedCRM}
                    onChange={(e) => {
                      setSelectedCRM(e.target.value as any);
                    }}
                  >
                    {activeCRMs?.length &&
                      activeCRMs.map(
                        (
                          crm: {
                            accountid: string;
                            id: number;
                            displayName: string;
                          },
                          index: number
                        ) => {
                          return (
                            <MenuItem value={crm.displayName} key={index}>
                              {crm.displayName}
                            </MenuItem>
                          );
                        }
                      )}
                  </Select>
                </FormControl>
              ) : (
                <div />
              )}
              {
                <Button
                  className={
                    disableButtons ? classes.buttonDisabled : classes.button
                  }
                  name="syncContacts"
                  variant="contained"
                  color="primary"
                  disabled={disableButtons}
                  onClick={() => {
                    // cannot use process.env here
                    VITE_APP_CONTACT_SYNC === '1'
                      ? handleSync(false)
                      : handleSync(true);
                  }}
                  disableElevation
                >
                  Sync Contacts
                </Button>
              }
              {process.env.VITE_APP_CONTACT_SYNC === '1' && (
                <Button
                  className={
                    disableButtons ? classes.buttonDisabled : classes.button
                  }
                  name="syncContacts"
                  variant="contained"
                  color="primary"
                  disabled={disableButtons}
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                  disableElevation
                >
                  Delete & Sync
                </Button>
              )}
            </div>
          )}
        </>
      }
    />
  );
  /**
   * Create column for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'created_at',
        headerName: 'Created',
        headerAlign: 'left',
        align: 'left',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        valueFormatter: (params) =>
          moment(params.row.created_at || undefined)?.format('MM/DD/YYYY'),
      },
      {
        field: 'crmname',
        headerName: 'CRM Name',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      // {
      //   field: 'name',
      //   headerName: 'Name',
      //   headerAlign: 'left',
      //   align: 'left',
      //   enableSorting: false,
      //   headerClassName: dataTableScss.fullWidth,
      //   cellClassName: dataTableScss.fullWidth,
      // },
      {
        field: 'firstName',
        headerName: 'First Name',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'phones',
        headerName: 'Phones',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        valueFormatter: (params) => params.row.phones?.[0] ?? '-',
      },
      {
        field: 'emails',
        headerName: 'Email(s)',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        valueFormatter: (params) => params.row.emails?.[0] ?? '-',
      },
      {
        field: 'company',
        headerName: 'Company',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        valueFormatter: (params) => params.row.company ?? '-',
      },
      {
        field: 'externalid',
        headerName: 'Contact ID',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'ownerid',
        headerName: 'CRM Owner ID',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'userid',
        headerName: 'Kixie User ID',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      // {
      //   field: 'id',
      //   headerName: 'ID',
      //   headerAlign: 'left',
      //   align: 'center',
      //   headerClassName: dataTableScss.fullWidth,
      //   cellClassName: dataTableScss.fullWidth,
      // },
      {
        field: 'deallink',
        headerName: 'Deal Link',
        headerAlign: 'left',
        align: 'left',
        enableSorting: false,
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        // valueFormatter: (params) => params.row.deallink?.split("'")[1],
        renderCell: (params) => {
          return (
            <Tooltip title={params.row.dealLink}>
              <a
                href={params.row.dealLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {params.row.dealId}
              </a>
            </Tooltip>
          );
        },
      },
    ],
    [classes, businessUsersLoadable, role, callRecord, businessDataLoadable]
  );
  return (
    <>
      <ModalWithStyles
        onSubmit={() => {
          handleSync(true);
        }}
        title="Delete & Sync Contacts"
        aria-labelledby="example-modal-sizes-title-md"
        show={confirmDelete}
        onHide={() => setConfirmDelete(false)}
        isSubmitting={syncLoading}
        isLoading={syncLoading}
        deleteDialog={true}
        size="xs"
      >
        <p>
          ALL contacts will be deleted. This operation could take some time
          depending on the number of contacts in the business. Once deletion is
          complete the contacts for the selected CRM will be synced. Are you
          sure you want to continue?
        </p>
      </ModalWithStyles>
      <DataTable
        hideLastPageButton={true}
        rows={rowsCached?.current ?? []}
        columns={columns}
        rowCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleGridSortModelChange}
        loading={contacts.state === 'loading'}
        ToolbarComponent={ToolbarComponent}
        pagination
      />
    </>
  );
};
